import { useSearch } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { Plus, MoreHorizontal } from "lucide-react";
import { useLingui } from "@lingui/react/macro";
import { RecipesMoreMenu } from "@/components/recipes";
import { getCreateLabel } from "@/components/recipes/tabs/tab-utils";

interface RecipesHeaderProps {
  onExport: () => void;
  /** Omitted when the user cannot import (Working mode hides recipe imports, YC-33). */
  onImport?: () => void;
  onShare: () => void;
  /** Omitted when the user cannot create anything here, which hides the button. */
  onCreateNew?: () => void;
  tablet?: boolean;
}

export function RecipesHeader({ onExport, onImport, onShare, onCreateNew, tablet = false }: RecipesHeaderProps) {
  const { t, i18n } = useLingui();
  const { tab } = useSearch({ from: "/(protected)/_layout/recipes/" });

  if (tablet) {
    return (
      <div className="flex h-15.5 items-center gap-3 border-b border-(--color-border) px-5">
        <h1 className="text-[1.375rem] font-extrabold tracking-tight leading-none text-(--color-text-primary)">
          {t`Recipes`}
        </h1>
        <div className="ml-auto flex items-center gap-2 shrink-0">
          <RecipesMoreMenu onExport={onExport} onImport={onImport} onShare={onShare}>
            <Button variant="outline" className="h-11 gap-2 rounded-[0.625rem] shadow-(--shadow-button)">
              <MoreHorizontal className="size-5" />
              {t`More`}
            </Button>
          </RecipesMoreMenu>
          {onCreateNew && (
            <Button className="h-11 gap-2 rounded-[0.625rem] shadow-(--shadow-button)" onClick={onCreateNew}>
              <Plus className="size-5" />
              {getCreateLabel(tab, i18n)}
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between px-3">
      <div>
        <h1 className="text-5xl font-bold tracking-tight text-(--color-text-primary)">
          {t`Recipes`}
        </h1>
        <p className="text-(--color-text-secondary) mt-1">
          {t`Manage all recipes across brands and locations`}
        </p>
      </div>
      <div className="flex gap-3">
        <RecipesMoreMenu onExport={onExport} onImport={onImport} onShare={onShare}>
          <Button variant="outline" className="h-13 gap-2 rounded-[0.625rem] shadow-(--shadow-button)">
            <MoreHorizontal className="size-5" />
            {t`More`}
          </Button>
        </RecipesMoreMenu>
        {onCreateNew && (
          <Button className="h-13 gap-2 rounded-[0.625rem] shadow-(--shadow-button)" onClick={onCreateNew}>
            <Plus className="size-5" />
            {getCreateLabel(tab, i18n)}
          </Button>
        )}
      </div>
    </div>
  );
}
